import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import _ from 'lodash';
import { PLANT_API_BASE_URL } from 'shared/constants/constants';

const initialState = {
  currentPath: '',
  creatingApplication: false,
  createdApplication: false,
  applicationId: null,
  licenceType: null,
  applicationNo: null,
  applicationFulfilled: false,
  storingAppPayment: false,
  applicationFinished: false,
  receivingApplication: false,
  receivedApplication: false,
  savingApplication: false,
  savedApplication: false,
  error: false,
  emailSent: false,
  emailSentTo: null,
  saveApplication: '',
};

const applicationSlice = createSlice({
  name: 'application',
  initialState,
  reducers: {
    storeCurrentPath(state, action) {
      const currentPath = action.payload;
      state.currentPath = currentPath;
    },
    clearApplication() {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getApplication.pending, (state, action) => {
        state.receivingApplication = true;
        state.receivedApplication = false;
        state.error = false;
      })
      .addCase(getApplication.fulfilled, (state, action) => {
        const payload = action.payload;
        return {
          ...payload,
          ...payload.applicationData.application,
          receivingApplication: false,
          savingApplication: false,
          receivedApplication: true,
          error: false,
        };
      })
      .addCase(getApplication.rejected, (state, action) => {
        state.receivingApplication = false;
        state.receivedApplication = false;
        state.error = true;
      })
      .addCase(createApplication.pending, (state, action) => {
        state.creatingApplication = true;
        state.createdApplication = false;
      })
      .addCase(createApplication.fulfilled, (state, action) => {
        state.storingAppPayment = false;
        state.applicationFinished = false;
        state.creatingApplication = false;
        state.createdApplication = true;
        state.savingApplication = false;
        state.savedApplication = false;
        state.error = false;

        if (!action.payload) return;

        state.applicationId = action.payload.applicationId;
        state.applicationNo = action.payload.applicationNo;
        state.licenceType = action.payload.licenceType;
      })
      .addCase(createApplication.rejected, (state, action) => {
        state.storingAppPayment = false;
        state.applicationFinished = false;
        state.creatingApplication = false;
        state.createdApplication = false;
        state.savingApplication = false;
        state.savedApplication = false;
        state.error = true;
      })
      .addCase(saveApplication.pending, (state, action) => {
        state.savingApplication = true;
        state.savedApplication = false;
        state.error = false;
      })
      .addCase(saveApplication.fulfilled, (state, action) => {
        state.savingApplication = false;
        state.savedApplication = true;
        state.error = false;

        if (!action.payload) return;

        state.emailSent = action.payload.emailSent;
        state.emailSentTo = action.payload.emailSentTo;
      })
      .addCase(saveApplication.rejected, (state, action) => {
        state.savingApplication = false;
        state.savedApplication = false;
        state.error = true;
      });
  },
});

const apiUrl = `${PLANT_API_BASE_URL}/applications/`;

export const getApplication = createAsyncThunk(
  'application/getApplication',
  async (applicationId) => {
    if (!applicationId) return;

    const res = await axios.get(apiUrl + applicationId, {
      headers: {
        'Content-Type': 'application/json',
      },
    });

    let applicationData = res.data.applicationData ? JSON.parse(res.data.applicationData) : {};
    return { ...res.data, applicationData };
  },
  {
    condition: (applicationId, { getState, extra }) => {
      const { application } = getState();
      //do not proceed if application has been created or in receiving state
      return application.applicationId != applicationId && !application.receivingApplication;
    },
  },
);

export const createApplication = createAsyncThunk(
  'application/createApplication',
  async (licenceType, { getState }) => {
    const state = getState();

    // Do not send the oidc information to the api because it can cause security issue
    const copiedData = { ...state };
    delete copiedData.snswOidc;
    delete copiedData.passwordlessOidc;
    delete copiedData.referenceData;
    delete copiedData.plantDesignReferenceData;

    const data = {
      licenceType,
      applicationData: JSON.stringify(copiedData),
    };

    const res = await axios.post(apiUrl, data);
    return { ...res.data, licenceType };
  },
  {
    condition: (licenceType, { getState, extra }) => {
      const { application } = getState();
      //do not proceed if application has been created or in receiving state
      const createApplication =
        application.licenceType !== licenceType &&
        !application.createdApplication &&
        !application.receivingApplication &&
        !application.receivedApplication &&
        !application.creatingApplication &&
        !application.applicationId;

      return createApplication;
    },
  },
);

export const saveApplication = createAsyncThunk(
  'application/saveApplication',
  async (sendEmail = true, { getState }) => {
    const application = getState().application;
    const applicantDetails = getState().applicantDetails;
    const plantDesignApplicantDetails = getState().plantDesignApplicantDetails;
    const passwordlessOidc = getState().passwordlessOidc;

    let email;
    let applicantName;

    if (passwordlessOidc?.user?.profile?.email) {
      applicantName = plantDesignApplicantDetails.firstName;
      email = passwordlessOidc?.user?.profile?.email;
    }

    const statedata = getState();
    // Do not send the oidc information to the api because it can cause security issue
    const copiedData = _.cloneDeep(statedata);
    delete copiedData.passwordlessOidc;
    delete copiedData.referenceData;
    delete copiedData.plantDesignReferenceData;
    delete copiedData.application.savingApplication;
    delete copiedData.application.creatingApplication;
    delete copiedData.application.receivingApplication;

    const data = {
      applicationId: application.applicationId,
      applicantName,
      applicationData: JSON.stringify(copiedData), //as per server api format, we need to stringify
      sendEmail: false,
      loggedInEmail: email,
    };

    data.sendEmail = sendEmail && email && (!application.emailSent || application.emailSentTo !== email); //do not send email if email previously sent.

    await axios.put(apiUrl, data);

    if (sendEmail) return { emailSent: true, emailSentTo: email };

    return {};
  },
  {
    condition: (_, { getState, extra }) => {
      const application = getState().application;
      return !!application?.applicationId;
    },
  },
);

export const { storeCurrentPath, clearApplication } = applicationSlice.actions;
export const applicationReducer = applicationSlice.reducer;
